import { useState } from 'react';
import { useCustom, useGo } from '@refinedev/core';
import {
  Search,
  Loader2,
  Users,
  UserCheck,
  TrendingUp,
  GraduationCap,
  Clock,
  BarChart2,
  Activity,
  PieChart as PieChartIcon,
  Flame,
  BellOff,
  Timer,
  Eye,
  BookCheck,
  CalendarClock,
  Infinity as InfinityIcon,
  AlertTriangle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Section, formatDate, formatCurrency } from '@/components/universal';
import { Separator } from '@/components/ui/separator';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';

// ── Interfaces ───────────────────────────────────────────────────────────────

interface CourseStudent {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  telegramId: string | null;
  accessStatus: string;
  accessSource: string;
  accessGrantedAt: string | null;
  accessExpiresAt: string | null;
  completedItems: number;
  totalItems: number;
  progressPercent: number;
  completedAt: string | null;
}

/** Отображаемое имя: ФИО → @username → email → user #id (не «Без имени» / ID: null). */
function studentDisplayName(student: CourseStudent): string {
  const fullName = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
  if (fullName.length > 0) {
    return fullName;
  }
  if (student.username) {
    return `@${student.username}`;
  }
  if (student.email) {
    return student.email;
  }
  return `Пользователь #${student.userId}`;
}

/** Вторая строка: контакт / идентификатор, без «ID: null». */
function studentSecondaryLine(student: CourseStudent): string {
  const fullName = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
  // Если в заголовке уже @username или email — не дублируем.
  if (fullName.length > 0) {
    if (student.username) {
      return `@${student.username}`;
    }
    if (student.email) {
      return student.email;
    }
    if (student.telegramId) {
      return `TG: ${student.telegramId}`;
    }
    return `#${student.userId}`;
  }
  if (student.username && student.email) {
    return student.email;
  }
  if (student.telegramId) {
    return `TG: ${student.telegramId}`;
  }
  return `#${student.userId}`;
}

function formatAccessUntil(iso: string | null): string {
  if (!iso) {
    return 'Бессрочно';
  }
  return formatDate(iso);
}

interface CourseStudentsResponse {
  data: CourseStudent[];
  total: number;
  page: number;
  limit: number;
}

interface BySource {
  subscription: number;
  order: number;
  manual: number;
  promotion: number;
  gift: number;
}

interface CourseAnalytics {
  courseId: number;
  access: {
    total: number;
    active: number;
    expired: number;
    bySource: BySource;
  };
  progress: { started: number; completed: number; completionRate: number; avgProgress: number };
  sales: { totalOrders: number; paidOrders: number; revenue: number; avgPrice: number };
  interest: { wishlistCount: number };
}

interface TimeseriesPoint {
  date: string;
  access: number;
  activity: number;
  revenue: number;
}

interface FunnelStep {
  lessonId: number;
  order: number;
  type: 'VIDEO' | 'ARTICLE';
  reached: number;
  completed: number;
}

interface ExtendedAnalytics {
  engagement: {
    everActive: number;
    active7d: number;
    active30d: number;
    silent: number;
  };
  expirations: {
    in7d: number;
    in30d: number;
    noExpiry: number;
  };
  pace: {
    avgDaysToComplete: number | null;
    completedWithDate: number;
  };
  videoDepth: {
    avgWatchPercent: number | null;
    samples: number;
  };
  homework: {
    hasHomework: boolean;
    submitted: number;
    reviewed: number;
    notStarted: number;
    submitRate: number;
  };
}

interface ModuleFunnelStep {
  moduleId: number;
  order: number;
  lessonsInModule: number;
  avgCompletedInModule: number;
  completionRate: number;
}

// ── Constants ────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  ORDER: 'Заказ',
  SUBSCRIPTION: 'Подписка',
  MANUAL: 'Ручной',
  PROMOTION: 'Промо',
  GIFT: 'Подарок',
};

const BY_SOURCE_KEYS: Array<keyof BySource> = [
  'order',
  'subscription',
  'manual',
  'promotion',
  'gift',
];

const DAYS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: '7 дней', value: '7' },
  { label: '30 дней', value: '30' },
  { label: '90 дней', value: '90' },
];

// ── Tooltip components ────────────────────────────────────────────────────────

interface TimeseriesPayloadItem {
  value: number;
  dataKey: string;
  color: string;
}

function TimeseriesTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TimeseriesPayloadItem[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0 || !label) { return null; }

  let dateLabel = label;
  try {
    dateLabel = format(parseISO(label), 'd MMMM yyyy', { locale: ru });
  } catch {
    // keep original label if parse fails
  }

  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium mb-1">{dateLabel}</p>
      {payload.map((item) => {
        const names: Record<string, string> = {
          access: 'Новые доступы',
          activity: 'Просмотры уроков',
        };
        return (
          <p key={item.dataKey} style={{ color: item.color }}>
            {names[item.dataKey] ?? item.dataKey}: {item.value}
          </p>
        );
      })}
    </div>
  );
}

interface FunnelPayloadItem {
  value: number;
  dataKey: string;
  color: string;
}

function FunnelTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: FunnelPayloadItem[];
}) {
  if (!active || !payload || payload.length === 0) { return null; }

  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm space-y-1">
      {payload.map((item) => {
        const names: Record<string, string> = {
          reached: 'Открыли',
          completed: 'Завершили',
        };
        return (
          <p key={item.dataKey} style={{ color: item.color }}>
            {names[item.dataKey] ?? item.dataKey}: {item.value}
          </p>
        );
      })}
    </div>
  );
}

interface ModuleFunnelPayloadItem {
  value: number;
  dataKey: string;
  color: string;
  payload: { moduleLabel: string; lessonsInModule: number };
}

function ModuleFunnelTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: ModuleFunnelPayloadItem[];
}) {
  if (!active || !payload || payload.length === 0) { return null; }

  const first = payload[0];
  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium mb-1">{first?.payload.moduleLabel}</p>
      <p className="text-muted-foreground text-xs">Уроков в модуле: {first?.payload.lessonsInModule}</p>
      {payload.map((item) => {
        const names: Record<string, string> = {
          completionRate: 'Завершили модуль (%)',
          avgCompletedInModule: 'Среднее пройдено уроков',
        };
        return (
          <p key={item.dataKey} style={{ color: item.color }}>
            {names[item.dataKey] ?? item.dataKey}: {typeof item.value === 'number' ? item.value.toFixed(1) : item.value}
          </p>
        );
      })}
    </div>
  );
}

// ── Props ────────────────────────────────────────────────────────────────────

interface CourseAnalyticsTabProps {
  courseId: string | number;
}

// ── Component ────────────────────────────────────────────────────────────────

export function CourseAnalyticsTab({ courseId }: CourseAnalyticsTabProps) {
  const go = useGo();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [page, setPage] = useState(1);
  const [days, setDays] = useState('30');
  const limit = 20;

  // ── Queries ─────────────────────────────────────────────────────────────────

  const analyticsQuery = useCustom<CourseAnalytics>({
    url: `/courses/${courseId}/analytics`,
    method: 'get',
  });
  const analytics = analyticsQuery.query?.data?.data;

  const timeseriesQuery = useCustom<TimeseriesPoint[]>({
    url: `/courses/${courseId}/analytics/timeseries`,
    method: 'get',
    config: {
      query: { days },
    },
  });
  const rawTimeseries = timeseriesQuery.query?.data?.data;
  const timeseriesData = (rawTimeseries ?? []).map((point) => ({
    ...point,
    formattedDate: (() => {
      try {
        return format(parseISO(point.date), 'd MMM', { locale: ru });
      } catch {
        return point.date;
      }
    })(),
  }));

  const funnelQuery = useCustom<FunnelStep[]>({
    url: `/courses/${courseId}/analytics/funnel`,
    method: 'get',
  });
  const rawFunnel = funnelQuery.query?.data?.data;
  const funnelData = (rawFunnel ?? []).map((step) => ({
    ...step,
    label: `Урок ${step.order}`,
  }));

  const extendedQuery = useCustom<ExtendedAnalytics>({
    url: `/courses/${courseId}/analytics/extended`,
    method: 'get',
  });
  const extended = extendedQuery.query?.data?.data;

  const moduleFunnelQuery = useCustom<ModuleFunnelStep[]>({
    url: `/courses/${courseId}/analytics/module-funnel`,
    method: 'get',
  });
  const rawModuleFunnel = moduleFunnelQuery.query?.data?.data;
  const moduleFunnelData = (rawModuleFunnel ?? []).map((m) => ({
    ...m,
    moduleLabel: `Модуль ${m.order + 1}`,
  }));

  const { query } = useCustom<CourseStudentsResponse>({
    url: `/courses/${courseId}/students`,
    method: 'get',
    config: {
      query: {
        search: search || undefined,
        status,
        page,
        limit,
      },
    },
  });

  const studentsResponse = query.data?.data;
  const students: CourseStudent[] = studentsResponse?.data ?? [];
  const total = studentsResponse?.total ?? students.length;
  const totalPages = Math.ceil(total / limit);
  const isLoading = query.isLoading;

  // ── Derived ──────────────────────────────────────────────────────────────────

  const totalRevenue = timeseriesData.reduce((sum, p) => sum + p.revenue, 0);
  const bySource = analytics?.access.bySource;

  const funnelBarHeight = 32;
  const funnelMinHeight = 200;
  const funnelHeight = Math.max(funnelMinHeight, funnelData.length * funnelBarHeight);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4 pt-6">
      {/* KPI cards */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
            <Users className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xl font-semibold">{analytics.access.active}</p>
              <p className="text-[11px] text-muted-foreground">Активных / {analytics.access.expired} истекло</p>
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
            <UserCheck className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xl font-semibold">{analytics.progress.started}</p>
              <p className="text-[11px] text-muted-foreground">Уникальные ученики · {analytics.progress.completed} завершили</p>
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
            <TrendingUp className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xl font-semibold text-green-600 dark:text-green-500">
                {formatCurrency(analytics.sales.revenue, 'UZS')}
              </p>
              <p className="text-[11px] text-muted-foreground">{analytics.sales.paidOrders} оплаченных</p>
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
            <GraduationCap className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xl font-semibold">{analytics.progress.completionRate}%</p>
              <p className="text-[11px] text-muted-foreground">{analytics.progress.completed} из {analytics.progress.started} завершили</p>
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xl font-semibold">{Math.round(analytics.progress.avgProgress)}%</p>
              <p className="text-[11px] text-muted-foreground">Средний прогресс</p>
            </div>
          </div>
        </div>
      )}

      {/* A) Engagement */}
      {extended && (
        <Section label="Вовлечённость">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <Flame className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{extended.engagement.everActive}</p>
                <p className="text-[11px] text-muted-foreground">Хоть раз открывали</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <Activity className="h-4 w-4 text-blue-500 shrink-0" />
              <div>
                <p className="text-xl font-semibold text-blue-600 dark:text-blue-400">{extended.engagement.active7d}</p>
                <p className="text-[11px] text-muted-foreground">Активны за 7 дней</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <UserCheck className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{extended.engagement.active30d}</p>
                <p className="text-[11px] text-muted-foreground">Активны за 30 дней</p>
              </div>
            </div>
            <div className={`rounded-xl border p-4 flex items-center gap-3 ${extended.engagement.silent > 0 ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800/50' : 'bg-card'}`}>
              <BellOff className={`h-4 w-4 shrink-0 ${extended.engagement.silent > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
              <div>
                <p className={`text-xl font-semibold ${extended.engagement.silent > 0 ? 'text-orange-600 dark:text-orange-400' : ''}`}>
                  {extended.engagement.silent}
                </p>
                <p className="text-[11px] text-muted-foreground">Остывшие (&gt;30д без активности)</p>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* C) Pace & video depth */}
      {extended && (
        <Section label="Темп и глубина">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <Timer className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">
                  {extended.pace.avgDaysToComplete !== null ? `${Math.round(extended.pace.avgDaysToComplete)} дн.` : '—'}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {extended.pace.avgDaysToComplete !== null
                    ? `Среднее время · ${extended.pace.completedWithDate} завершили`
                    : 'Нет завершивших с датой'}
                </p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xl font-semibold">
                    {extended.videoDepth.avgWatchPercent !== null ? `${Math.round(extended.videoDepth.avgWatchPercent)}%` : '—'}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Средний досмотр видео · {extended.videoDepth.samples} записей
                  </p>
                </div>
              </div>
              {extended.videoDepth.avgWatchPercent !== null && (
                <Progress value={extended.videoDepth.avgWatchPercent} className="h-1.5" />
              )}
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <GraduationCap className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{analytics?.progress.completionRate ?? 0}%</p>
                <p className="text-[11px] text-muted-foreground">Завершили курс</p>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Timeseries chart */}
      <Section label="Динамика активности">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className="h-4 w-4" />
            <span>Новые доступы и просмотры уроков</span>
          </div>
          <div className="flex items-center gap-3">
            {totalRevenue > 0 && (
              <div className="flex items-center gap-1.5 text-sm">
                <TrendingUp className="h-3.5 w-3.5 text-green-500" />
                <span className="text-muted-foreground">Выручка за период:</span>
                <span className="font-medium text-green-600 dark:text-green-500">
                  {formatCurrency(totalRevenue, 'UZS')}
                </span>
              </div>
            )}
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[110px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {timeseriesQuery.query?.isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!timeseriesQuery.query?.isLoading && timeseriesData.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            Нет данных за выбранный период
          </div>
        )}
        {!timeseriesQuery.query?.isLoading && timeseriesData.length > 0 && (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={timeseriesData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorAccess" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="formattedDate"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12 }}
                tickMargin={8}
                interval="preserveStartEnd"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12 }}
                tickFormatter={(value: number) =>
                  new Intl.NumberFormat('ru-RU', {
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(value)
                }
                width={40}
              />
              <Tooltip content={<TimeseriesTooltip />} />
              <Legend
                formatter={(value: string) =>
                  value === 'access' ? 'Новые доступы' : 'Просмотры уроков'
                }
                wrapperStyle={{ fontSize: 12 }}
              />
              <Area
                type="monotone"
                dataKey="access"
                stroke="#3b82f6"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorAccess)"
              />
              <Area
                type="monotone"
                dataKey="activity"
                stroke="#a855f7"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorActivity)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* Воронка по модулям */}
      <Section label="Воронка по модулям">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <BarChart2 className="h-4 w-4" />
          <span>Процент учеников, завершивших каждый модуль</span>
        </div>

        {moduleFunnelQuery.query?.isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!moduleFunnelQuery.query?.isLoading && moduleFunnelData.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            Нет данных по модулям
          </div>
        )}
        {!moduleFunnelQuery.query?.isLoading && moduleFunnelData.length > 0 && (
          <ResponsiveContainer width="100%" height={Math.max(200, moduleFunnelData.length * 52 + 40)}>
            <BarChart
              data={moduleFunnelData}
              layout="vertical"
              margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
              barCategoryGap="30%"
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-muted" />
              <XAxis
                type="number"
                domain={[0, 100]}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
                tickFormatter={(value: number) => `${value}%`}
              />
              <YAxis
                type="category"
                dataKey="moduleLabel"
                width={80}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
              />
              <Tooltip content={<ModuleFunnelTooltip />} />
              <Legend
                formatter={(value: string) =>
                  value === 'completionRate' ? 'Завершили модуль (%)' : 'Среднее пройдено уроков'
                }
                wrapperStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="completionRate" fill="#22c55e" radius={[0, 4, 4, 0]} maxBarSize={20} label={{ position: 'right', fontSize: 11, formatter: (v: number) => `${v.toFixed(0)}%` }} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* Воронка по урокам */}
      <Section label="Воронка по урокам">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <BarChart2 className="h-4 w-4" />
          <span>Сколько учеников открыли и завершили каждый урок</span>
        </div>

        {funnelQuery.query?.isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!funnelQuery.query?.isLoading && funnelData.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            Нет данных по урокам
          </div>
        )}
        {!funnelQuery.query?.isLoading && funnelData.length > 0 && (
          <div
            className="overflow-y-auto"
            style={{ maxHeight: 480 }}
          >
            <div style={{ height: funnelHeight, minHeight: funnelMinHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={funnelData}
                  layout="vertical"
                  margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
                  barCategoryGap="20%"
                  barGap={2}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-muted" />
                  <XAxis
                    type="number"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value: number) =>
                      new Intl.NumberFormat('ru-RU', {
                        notation: 'compact',
                        compactDisplay: 'short',
                      }).format(value)
                    }
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={72}
                    tickLine={false}
                    axisLine={false}
                    tick={({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => {
                      const step = funnelData.find((s) => s.label === payload.value);
                      const isVideo = step?.type === 'VIDEO';
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text
                            x={-4}
                            y={0}
                            dy={4}
                            textAnchor="end"
                            fontSize={11}
                            fill="currentColor"
                            className="fill-muted-foreground"
                          >
                            {isVideo ? '▶ ' : '📄 '}{payload.value}
                          </text>
                        </g>
                      );
                    }}
                  />
                  <Tooltip content={<FunnelTooltip />} />
                  <Legend
                    formatter={(value: string) =>
                      value === 'reached' ? 'Открыли' : 'Завершили'
                    }
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  <Bar dataKey="reached" fill="#3b82f6" radius={[0, 3, 3, 0]} maxBarSize={14} />
                  <Bar dataKey="completed" fill="#22c55e" radius={[0, 3, 3, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Section>

      {/* Распределение доступов по источникам */}
      {bySource && (
        <Section label="Распределение доступов по источникам">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <PieChartIcon className="h-4 w-4" />
            <span>Как были выданы доступы к курсу</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {BY_SOURCE_KEYS.map((key) => {
              const count = bySource[key];
              const labelKey = key.toUpperCase();
              const label = SOURCE_LABELS[labelKey] ?? labelKey;
              const sourceTotal = BY_SOURCE_KEYS.reduce((sum, k) => sum + bySource[k], 0);
              const percent = sourceTotal > 0 ? Math.round((count / sourceTotal) * 100) : 0;
              return (
                <div key={key} className="rounded-xl border bg-card p-4 flex flex-col gap-1.5">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xl font-semibold">{count}</p>
                  <div className="flex items-center gap-1.5">
                    <Progress value={percent} className="h-1 flex-1" />
                    <span className="text-[10px] tabular-nums text-muted-foreground">{percent}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* D) Истечения доступов */}
      {extended && (
        <Section label="Истечения доступов">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className={`rounded-xl border p-4 flex items-center gap-3 ${extended.expirations.in7d > 0 ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800/50' : 'bg-card'}`}>
              <AlertTriangle className={`h-4 w-4 shrink-0 ${extended.expirations.in7d > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
              <div>
                <p className={`text-xl font-semibold ${extended.expirations.in7d > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {extended.expirations.in7d}
                </p>
                <p className="text-[11px] text-muted-foreground">Истекает за 7 дней</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{extended.expirations.in30d}</p>
                <p className="text-[11px] text-muted-foreground">Истекает за 30 дней</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <InfinityIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{extended.expirations.noExpiry}</p>
                <p className="text-[11px] text-muted-foreground">Бессрочных доступов</p>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* E) Домашние задания */}
      {extended?.homework.hasHomework && (
        <Section label="Домашние задания">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <BookCheck className="h-4 w-4" />
            <span>Статистика выполнения домашних заданий</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border bg-card p-4 flex flex-col gap-1.5">
              <p className="text-xs text-muted-foreground">Сдали ДЗ</p>
              <p className="text-xl font-semibold">{extended.homework.submitted}</p>
              <div className="flex items-center gap-1.5">
                <Progress value={extended.homework.submitRate} className="h-1 flex-1" />
                <span className="text-[10px] tabular-nums text-muted-foreground">{extended.homework.submitRate}%</span>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <UserCheck className="h-4 w-4 text-green-500 shrink-0" />
              <div>
                <p className="text-xl font-semibold text-green-600 dark:text-green-400">{extended.homework.reviewed}</p>
                <p className="text-[11px] text-muted-foreground">Проверено</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{extended.homework.submitted - extended.homework.reviewed}</p>
                <p className="text-[11px] text-muted-foreground">Ожидают проверки</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <BellOff className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{extended.homework.notStarted}</p>
                <p className="text-[11px] text-muted-foreground">Не приступили</p>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Поиск: имя, @username, email, id…"
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Все доступы</SelectItem>
            <SelectItem value="ACTIVE">Активные</SelectItem>
            <SelectItem value="EXPIRED">Истекшие</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Access table */}
      <Section label={`Доступы к курсу · ${total}`}>
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && students.length === 0 && (
          <div className="text-center text-muted-foreground py-12 text-sm">
            {search ? 'Ничего не найдено' : 'Нет доступов'}
          </div>
        )}
        {!isLoading && students.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Пользователь</TableHead>
                  <TableHead>Источник</TableHead>
                  <TableHead>Прогресс</TableHead>
                  <TableHead>Уроки</TableHead>
                  <TableHead>Доступ с</TableHead>
                  <TableHead>Доступ до</TableHead>
                  <TableHead>Статус</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student) => {
                  const name = studentDisplayName(student);
                  const secondary = studentSecondaryLine(student);
                  const isRevoked = student.accessStatus === 'REVOKED';
                  const isExpired = student.accessStatus === 'EXPIRED' || isRevoked;
                  const expiresAtMs = student.accessExpiresAt
                    ? new Date(student.accessExpiresAt).getTime()
                    : null;
                  const expiresSoon =
                    !isExpired
                    && expiresAtMs !== null
                    && expiresAtMs > Date.now()
                    && expiresAtMs - Date.now() <= 7 * 24 * 60 * 60 * 1000;
                  const untilClassName = expiresSoon
                    ? 'text-xs whitespace-nowrap text-orange-600 dark:text-orange-400 font-medium'
                    : 'text-xs whitespace-nowrap text-muted-foreground';

                  let statusBadge = (
                    <Badge variant="outline" className="text-[10px]">Активен</Badge>
                  );
                  if (isRevoked) {
                    statusBadge = <Badge variant="secondary" className="text-[10px]">Отозван</Badge>;
                  } else if (isExpired) {
                    statusBadge = <Badge variant="secondary" className="text-[10px]">Истёк</Badge>;
                  } else if (expiresSoon) {
                    statusBadge = (
                      <Badge variant="outline" className="text-[10px] border-orange-400 text-orange-600 dark:text-orange-400">
                        Истекает
                      </Badge>
                    );
                  } else if (student.completedAt) {
                    statusBadge = <Badge variant="default" className="text-[10px]">Завершён</Badge>;
                  }

                  return (
                    <TableRow
                      key={student.userId}
                      className="cursor-pointer"
                      onClick={() => go({ to: `/users/show/${student.userId}` })}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{name}</p>
                          <p className="text-xs text-muted-foreground">{secondary}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {SOURCE_LABELS[student.accessSource] ?? student.accessSource}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={student.progressPercent} className="h-1.5 flex-1" />
                          <span className="text-xs tabular-nums text-muted-foreground w-7 text-right">
                            {student.progressPercent}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {student.completedItems} / {student.totalItems}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {student.accessGrantedAt ? formatDate(student.accessGrantedAt) : '—'}
                      </TableCell>
                      <TableCell className={untilClassName}>
                        {formatAccessUntil(student.accessExpiresAt)}
                      </TableCell>
                      <TableCell>{statusBadge}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{page} / {totalPages}</p>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      Назад
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                      Далее
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
